import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { CalendarDays, Users, Clock, DollarSign, TrendingUp, Activity, Plus, Receipt, UserPlus, Scissors, ArrowRight, User } from 'lucide-react';
import { format } from 'date-fns';
import { vi as viLocale } from 'date-fns/locale/vi';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dateLocale = i18n.language === 'vi' ? { locale: viLocale } : {};
  const fmt = (date, pattern) => format(date, pattern, dateLocale);
  const [stats, setStats] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/stats`, { withCredentials: true });
      setStats(data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const statusColor = {
    confirmed: 'bg-[#556B2F] text-white',
    completed: 'bg-[#3E6641] text-white',
    cancelled: 'bg-[#E2725B] text-white',
    'no-show': 'bg-[#D4A373] text-white',
    arrived: 'bg-[#2E86C1] text-white',
    started: 'bg-[#8E44AD] text-white'
  };

  const sales7 = stats?.sales_7days || [];
  const upcoming7 = stats?.upcoming_7days || [];
  const activity = stats?.recent_activity || [];
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const todayAll = activity.filter(a => a.date === todayStr);
  const todayUpcoming = todayAll.filter(a => a.status === 'confirmed');
  const nowH = new Date().getHours();
  const nowM = new Date().getMinutes();
  const nextAppt = todayUpcoming.find(a => {
    const [h, m] = (a.start_time || '00:00').split(':').map(Number);
    return h > nowH || (h === nowH && m > nowM);
  });

  return (
    <div className="fade-in space-y-6" data-testid="dashboard-page">
      {/* Header + Quick Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-medium text-[#1A2421] tracking-tight" style={{ fontFamily: 'Outfit' }}>{t('dashboard.title')}</h1>
          <p className="text-sm text-[#5C6656] mt-1">{fmt(new Date(), 'EEEE, d MMMM yyyy')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={() => navigate('/appointments')} className="bg-[#556B2F] hover:bg-[#4A5D23] text-white h-9 text-xs gap-1.5" data-testid="quick-book-btn">
            <Plus size={14} /> New Booking
          </Button>
          <Button onClick={() => navigate('/appointments')} variant="outline" className="border-[#1A2421] text-[#1A2421] hover:bg-[#1A2421] hover:text-white h-9 text-xs gap-1.5" data-testid="quick-sale-btn">
            <Receipt size={14} /> Quick Sale
          </Button>
          <Button onClick={() => navigate('/clients')} variant="outline" className="border-[#E5E7E1] text-[#5C6656] hover:border-[#556B2F] h-9 text-xs gap-1.5" data-testid="quick-client-btn">
            <UserPlus size={14} /> Add Client
          </Button>
        </div>
      </div>

      {/* Next Appointment Banner */}
      {nextAppt && (
        <div className="bg-gradient-to-r from-[#556B2F] to-[#3E6641] rounded-xl p-5 text-white flex items-center justify-between" data-testid="next-appt-banner">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider opacity-80">Next appointment</p>
            <p className="text-lg font-bold mt-1" style={{ fontFamily: 'Outfit' }}>{nextAppt.start_time} — {nextAppt.client_name}</p>
            <p className="text-sm opacity-80">{nextAppt.service_name} with {nextAppt.staff_name}</p>
          </div>
          <Button onClick={() => navigate('/appointments')} variant="ghost" className="text-white border border-white/30 hover:bg-white/10 h-9 text-xs gap-1">
            View Calendar <ArrowRight size={14} />
          </Button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: t('dashboard.todaysBookings'), value: stats?.today_appointments || 0, icon: CalendarDays, sub: `${todayAll.filter(a => a.status === 'completed').length} completed`, color: '#556B2F' },
          { label: t('dashboard.upcomingToday'), value: todayUpcoming.length, icon: Clock, sub: 'confirmed', color: '#2E86C1' },
          { label: t('dashboard.todaysRevenue'), value: `\u00A3${(stats?.today_revenue || 0).toFixed(2)}`, icon: DollarSign, sub: `${stats?.completed || 0} sales`, color: '#3E6641' },
          { label: t('dashboard.totalClients'), value: stats?.total_clients || 0, icon: Users, sub: t('dashboard.allTime'), color: '#D4A373' },
        ].map((item, i) => {
          const Icon = item.icon;
          return (
            <Card key={i} className="border-[#E5E7E1] shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-bold tracking-wider uppercase text-[#5C6656]">{item.label}</p>
                    <p className="text-2xl font-bold text-[#1A2421] mt-1" style={{ fontFamily: 'Outfit' }}>{item.value}</p>
                    <p className="text-[10px] text-[#5C6656] mt-0.5">{item.sub}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${item.color}15` }}>
                    <Icon size={20} style={{ color: item.color }} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Sales Chart */}
        <Card className="border-[#E5E7E1] shadow-sm" data-testid="recent-sales-chart">
          <CardHeader className="pb-1">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('dashboard.recentSales')}</CardTitle>
              <span className="text-xs text-[#5C6656]">{t('dashboard.last7Days')}</span>
            </div>
            <span className="text-2xl font-bold text-[#1A2421]">{'\u00A3'}{sales7.reduce((s, d) => s + d.revenue, 0).toFixed(2)}</span>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={sales7}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7E1" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#5C6656' }} tickFormatter={d => { try { return fmt(new Date(d + 'T12:00:00'), 'EEE'); } catch { return d; } }} />
                <YAxis tick={{ fontSize: 10, fill: '#5C6656' }} tickFormatter={v => `\u00A3${v}`} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E5E7E1', fontSize: 12 }} formatter={(v) => [`\u00A3${v.toFixed(2)}`, 'Revenue']} />
                <Line type="monotone" dataKey="revenue" stroke="#556B2F" strokeWidth={2.5} dot={{ r: 3, fill: '#556B2F' }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Bookings Chart */}
        <Card className="border-[#E5E7E1] shadow-sm" data-testid="upcoming-appointments-chart">
          <CardHeader className="pb-1">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('dashboard.upcomingBookings')}</CardTitle>
              <span className="text-xs text-[#5C6656]">{t('dashboard.next7Days')}</span>
            </div>
            <span className="text-2xl font-bold text-[#1A2421]">{upcoming7.reduce((s, d) => s + d.booked, 0)} booked</span>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={upcoming7}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7E1" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#5C6656' }} tickFormatter={d => { try { return fmt(new Date(d + 'T12:00:00'), 'EEE'); } catch { return d; } }} />
                <YAxis tick={{ fontSize: 10, fill: '#5C6656' }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E5E7E1', fontSize: 12 }} />
                <Bar dataKey="confirmed" fill="#556B2F" radius={[4, 4, 0, 0]} name="Confirmed" />
                <Bar dataKey="cancelled" fill="#E2725B" radius={[4, 4, 0, 0]} name="Cancelled" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Today's Schedule + Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-[#E5E7E1] shadow-sm" data-testid="todays-schedule">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base text-[#1A2421] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <CalendarDays size={16} className="text-[#556B2F]" /> Today's Schedule
              </CardTitle>
              <Button onClick={() => navigate('/appointments')} variant="ghost" size="sm" className="text-xs text-[#556B2F] h-7">View all</Button>
            </div>
          </CardHeader>
          <CardContent>
            {todayAll.length === 0 ? (
              <div className="text-center py-8">
                <CalendarDays size={32} className="mx-auto mb-2 text-[#E5E7E1]" />
                <p className="text-sm text-[#5C6656]">No appointments today</p>
                <Button onClick={() => navigate('/appointments')} className="mt-3 bg-[#556B2F] text-white h-8 text-xs">Book one now</Button>
              </div>
            ) : (
              <div className="space-y-1.5 max-h-[340px] overflow-y-auto pr-1">
                {todayAll.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')).map(a => {
                  const isPast = (() => { const [h,m] = (a.start_time||'00:00').split(':').map(Number); return h < nowH || (h === nowH && m <= nowM); })();
                  return (
                    <div key={a.id} className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${a.status === 'completed' ? 'border-[#E5E7E1] bg-[#F9F8F6]' : isPast ? 'border-[#E5E7E1] bg-[#FAFAF8]' : 'border-[#556B2F]/20 bg-white'}`}>
                      <div className="text-center w-12 flex-shrink-0">
                        <p className={`text-xs font-bold ${isPast && a.status !== 'completed' ? 'text-[#5C6656]' : 'text-[#556B2F]'}`}>{a.start_time}</p>
                        <p className="text-[9px] text-[#5C6656]">{a.end_time}</p>
                      </div>
                      <div className="w-0.5 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: a.status === 'completed' ? '#3E6641' : a.status === 'cancelled' ? '#E2725B' : '#556B2F' }} />
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium truncate ${a.status === 'completed' ? 'text-[#5C6656] line-through' : 'text-[#1A2421]'}`}>{a.client_name}</p>
                        <p className="text-[10px] text-[#5C6656] truncate">{a.service_name} · {a.staff_name}</p>
                      </div>
                      <Badge className={`text-[8px] capitalize flex-shrink-0 ${statusColor[a.status] || 'bg-gray-100'}`}>{a.status}</Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-[#E5E7E1] shadow-sm" data-testid="recent-activity">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-[#1A2421] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Activity size={16} className="text-[#556B2F]" /> {t('dashboard.recentActivity')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5 max-h-[340px] overflow-y-auto pr-1">
              {activity.length === 0 ? (
                <p className="text-sm text-[#5C6656] text-center py-8">{t('dashboard.noActivity')}</p>
              ) : activity.slice(0, 15).map(a => (
                <div key={a.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-[#E5E7E1] hover:border-[#556B2F]/20 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-[#E9EDDF] flex items-center justify-center flex-shrink-0">
                    <User size={14} className="text-[#556B2F]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#1A2421] truncate">{a.client_name} — {a.service_name}</p>
                    <p className="text-[10px] text-[#5C6656] truncate">{a.date} {a.start_time} · {a.staff_name}</p>
                  </div>
                  <Badge className={`text-[8px] capitalize flex-shrink-0 ${statusColor[a.status] || 'bg-gray-100'}`}>{a.status}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
